
import { useState, useEffect, useMemo } from "react";
import { EtsyProduct } from "@/hooks/useEtsyProducts";
import ProductFilters, { ProductFilters as FilterType } from "./ProductFilters";

interface ProductsFiltersContainerProps {
  products: EtsyProduct[];
  onFilteredProductsChange: (products: EtsyProduct[], hasActiveFilters: boolean) => void;
}

const ProductsFiltersContainer = ({ products, onFilteredProductsChange }: ProductsFiltersContainerProps) => {
  // Initialize filters
  const maxPrice = Math.max(...products.map(p => p.price || 0), 1000);
  const maxSales = Math.max(...products.map(p => p.salesCount || 0), 100);
  
  const [filters, setFilters] = useState<FilterType>({
    search: "",
    sortBy: "title",
    sortOrder: "asc",
    priceRange: { min: 0, max: maxPrice },
    stockStatus: "all",
    state: "all",
    salesRange: { min: 0, max: maxSales }
  });

  // Update filters when products change
  useEffect(() => {
    const newMaxPrice = Math.max(...products.map(p => p.price || 0), 1000);
    const newMaxSales = Math.max(...products.map(p => p.salesCount || 0), 100);
    
    setFilters(prev => ({
      ...prev,
      priceRange: { ...prev.priceRange, max: prev.priceRange.max === maxPrice ? newMaxPrice : prev.priceRange.max },
      salesRange: { ...prev.salesRange, max: prev.salesRange.max === maxSales ? newMaxSales : prev.salesRange.max }
    }));
  }, [products]);

  // Filter and sort products
  const filteredAndSortedProducts = useMemo(() => {
    let filtered = products.filter(product => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch = product.title.toLowerCase().includes(searchLower) ||
          (product.description && product.description.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }

      // Price range filter
      const price = product.price || 0;
      if (price < filters.priceRange.min || price > filters.priceRange.max) return false;

      // Sales range filter
      const sales = product.salesCount || 0;
      if (sales < filters.salesRange.min || sales > filters.salesRange.max) return false;

      // Stock status filter
      if (filters.stockStatus !== 'all') {
        const quantity = product.quantity;
        switch (filters.stockStatus) {
          case 'in-stock':
            if (!quantity || quantity <= 5) return false;
            break;
          case 'low-stock':
            if (!quantity || quantity === 0 || quantity > 5) return false;
            break;
          case 'out-of-stock':
            if (quantity !== 0) return false;
            break;
          case 'unknown':
            if (quantity !== undefined) return false;
            break;
        }
      }

      // State filter
      if (filters.state !== 'all') {
        if (product.state !== filters.state) return false;
      }

      return true;
    });

    // Sort products
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (filters.sortBy) {
        case 'title':
          aValue = a.title;
          bValue = b.title;
          break;
        case 'price':
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case 'quantity':
          aValue = a.quantity || 0;
          bValue = b.quantity || 0;
          break;
        case 'salesCount':
          aValue = a.salesCount || 0;
          bValue = b.salesCount || 0;
          break;
        case 'createdAt':
          aValue = new Date(a.createdAt);
          bValue = new Date(b.createdAt);
          break;
        case 'state':
          aValue = a.state || '';
          bValue = b.state || '';
          break;
        default:
          aValue = a.title;
          bValue = b.title;
      }

      if (typeof aValue === 'string') {
        const comparison = aValue.localeCompare(bValue);
        return filters.sortOrder === 'asc' ? comparison : -comparison;
      } else {
        const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        return filters.sortOrder === 'asc' ? comparison : -comparison;
      }
    });

    return filtered;
  }, [products, filters]);

  const clearFilters = () => {
    setFilters({
      search: "",
      sortBy: "title",
      sortOrder: "asc",
      priceRange: { min: 0, max: maxPrice },
      stockStatus: "all",
      state: "all",
      salesRange: { min: 0, max: maxSales }
    });
  };

  const hasActiveFilters = Boolean(filters.search || 
    filters.stockStatus !== 'all' || 
    filters.state !== 'all' ||
    filters.priceRange.min > 0 || 
    filters.priceRange.max < maxPrice ||
    filters.salesRange.min > 0 ||
    filters.salesRange.max < maxSales);

  // Notify parent of filtered products changes
  useEffect(() => {
    onFilteredProductsChange(filteredAndSortedProducts, hasActiveFilters);
  }, [filteredAndSortedProducts, hasActiveFilters, onFilteredProductsChange]);

  return (
    <ProductFilters 
      products={products}
      filters={filters}
      onFiltersChange={setFilters}
      onClearFilters={clearFilters}
    />
  );
};

export default ProductsFiltersContainer;
